/**
 * Utility functions for guest logout localStorage cleanup
 */

import { Logger } from '../shared/services/logger.service';
import { clearRcCredentials } from '@/apps/rocketchat/services/rcCredentials';

/**
 * Check if the current user is a guest
 * Returns true if user is a guest, false if user is a host
 */
export const isCurrentUserGuest = (): boolean => {
  try {
    // Check for guest token in localStorage
    const hasGuestToken = !!localStorage.getItem('isUserGuest');

    // Check for guest store data
    const guestStoreData = localStorage.getItem('guest-store');
    const hasGuestStore = guestStoreData && JSON.parse(guestStoreData).state?.guest;

    // Check if current route is a guest route
    const isGuestRoute =
      window.location.pathname.includes('/welcome') ||
      window.location.pathname.includes('/guest-workplace') ||
      window.location.pathname.includes('/guest-lobby');

    return hasGuestToken || hasGuestStore || isGuestRoute;
  } catch (error) {
    console.error('Error checking if user is guest:', error);
    return false;
  }
};

/**
 * Clears all guest-related localStorage items completely
 * This function removes all items that are related to guest sessions
 * IMPORTANT: Does NOT remove Cognito tokens or other user authentication data
 */
export const clearGuestLocalStorage = (): void => {
  try {
    Logger.debug('🧹 Clearing all guest-related localStorage items...');

    // Remove specific known guest-related items
    // These are items that should ALWAYS be cleared after guest logout
    const knownKeys = [
      'guestToken',
      'guestRefreshToken',
      'guest-store',
      'guest-workplace-store', // Guest workplace data (presence, floor, rooms, etc.)
      'panelPositions',
      'callStatsUserName',
      'jitsiMeetId',
      'session-storage', // Session data that persists after guest logout
      'workplace-store', // Workplace data that persists after guest logout
      'orgPid', // Organization PID set during guest session
      'org-storage', // Organization storage data
      'isUserGuest', // Guest flag
    ];

    knownKeys.forEach((key) => {
      localStorage.removeItem(key);
      Logger.debug(`🗑️ Removed localStorage key: ${key}`);
    });

    // NGE-681: the auditorium guest stash lives in sessionStorage, which survives a
    // guest logout for the lifetime of the tab. Left behind, the next (normal) guest in
    // the same tab is treated as auditorium audience and every join resolves to the
    // auditorium room instead of the room they were summoned to.
    sessionStorage.removeItem('auditoriumContext');

    clearRcCredentials();

    // Clear any other guest-related items that might exist
    // BUT be more specific to avoid deleting user auth data
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        // Only remove keys that are clearly guest-specific
        // Exclude Cognito tokens and other critical auth data
        const isGuestKey =
          (key.includes('guest') || key.includes('Guest')) &&
          !key.includes('CognitoIdentityServiceProvider');

        const isGuestJitsiKey = key.includes('jitsi') || key.includes('Jitsi');

        const isGuestPanelKey =
          key.includes('panel') && !key.includes('user') && !key.includes('workplace');

        const isGuestCallKey = key.includes('callStats') || key.includes('jitsiMeet');

        if (isGuestKey || isGuestJitsiKey || isGuestPanelKey || isGuestCallKey) {
          keysToRemove.push(key);
        }
      }
    }

    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
      Logger.debug(`🗑️ Removed localStorage key: ${key}`);
    });

    Logger.debug('✅ Guest localStorage cleanup completed');
  } catch (error) {
    console.error('❌ Error during guest localStorage cleanup:', error);
  }
};

/**
 * Clears all localStorage items (nuclear option)
 * Use this only when you want to clear everything
 */
export const clearAllLocalStorage = (): void => {
  try {
    Logger.debug('🧹 Clearing ALL localStorage items...');
    localStorage.clear();
    Logger.debug('✅ All localStorage items cleared');
  } catch (error) {
    console.error('❌ Error clearing all localStorage:', error);
  }
};

/**
 * sessionStorage marker set by every guest-logout path just before it lands on the
 * welcome page.
 *
 * The welcome page runs member auto-login (useAutoLoginEffect). A guest who has just
 * logged out usually has no Cognito token, but a leftover one from an earlier member
 * session in the same browser survives clearGuestLocalStorage by design — and then
 * /welcome/auth/init fails and WelcomePage sends them to the member sign-in form
 * instead of the guest form they logged out of. This marker tells WelcomePage to skip
 * the member auto-login for that one landing.
 *
 * Per-tab and consumed on read, so it never leaks into a later visit.
 */
const GUEST_LOGGED_OUT_KEY = 'guestLoggedOut';
const GUEST_LOGGED_OUT_MESSAGE_KEY = 'guestLoggedOutMessage';

export interface GuestLoggedOutMark {
  /** A guest logged out (or was logged off) immediately before this page load. */
  loggedOut: boolean;
  /** Notice to show on the welcome page, e.g. a host-initiated termination. */
  message: string | null;
}

/**
 * Mark that the next welcome-page landing is a guest who just logged out.
 *
 * `message` is carried across because the redirect is a full page load: a snackbar
 * raised just before it never gets shown, so the notice has to be re-raised on the
 * other side.
 */
export const markGuestLoggedOut = (message?: string): void => {
  try {
    sessionStorage.setItem(GUEST_LOGGED_OUT_KEY, 'true');
    if (message) sessionStorage.setItem(GUEST_LOGGED_OUT_MESSAGE_KEY, message);
  } catch {
    /* best-effort — the marker is an optimisation, not a correctness requirement */
  }
};

/** Read and clear the marker. Set only on the first read after a guest logout. */
export const consumeGuestLoggedOutMark = (): GuestLoggedOutMark => {
  try {
    const loggedOut = sessionStorage.getItem(GUEST_LOGGED_OUT_KEY) === 'true';
    const message = sessionStorage.getItem(GUEST_LOGGED_OUT_MESSAGE_KEY);
    if (loggedOut) sessionStorage.removeItem(GUEST_LOGGED_OUT_KEY);
    if (message) sessionStorage.removeItem(GUEST_LOGGED_OUT_MESSAGE_KEY);
    return { loggedOut, message };
  } catch {
    return { loggedOut: false, message: null };
  }
};

/**
 * Guards against the two logout handlers (OfficeEventRouter and GuestEventRouter both
 * listen for GuestCtrlLogoff) racing each other into two redirects.
 */
let guestLogoutRedirectStarted = false;

/**
 * Clear the guest session and land on the org's guest login form.
 *
 * Every caller must reach this even when its API calls fail: LoggedOutGuard bounces
 * /welcome straight back to /guest-workplace while `isUserGuest` is still in
 * localStorage, so a redirect without the cleanup leaves the guest exactly where they
 * were. Cleanup first, navigate second, and never behind a try that an API call can
 * throw out of.
 */
export const forceGuestLogoutRedirect = (message?: string): void => {
  if (guestLogoutRedirectStarted) {
    Logger.debug('[guestLogout] ⏭️ Redirect already in progress, skipping duplicate');
    return;
  }
  guestLogoutRedirectStarted = true;

  // Read the slug before the cleanup, and fall back to the path for the invite-link
  // guests who never had one stored.
  const storedSlug = localStorage.getItem('org-slug');
  const pathSlug = window.location.pathname.split('/')[1];
  const candidate = storedSlug || pathSlug;
  const orgSlug =
    candidate && candidate !== 'undefined' && candidate !== 'welcome' ? candidate : null;

  clearGuestLocalStorage();
  markGuestLoggedOut(message);

  window.location.replace(orgSlug ? `/${orgSlug}/welcome` : '/welcome');
};
